import type { GameState, JudgmentResult, SaveData } from "../types";
import { resolveFinalEnding } from "./endingResolver";
import { generateNextMail } from "./roundGenerator";
import { resolvePhase } from "./progression";
import { createInitialGameState } from "./gameState";

export function startRun(seed?: number): GameState {
  return generateNextMail(createInitialGameState(seed));
}

function recordJudgment(
  state: GameState,
  playerSelectedAnomaly: boolean,
  correct: boolean
): GameState {
  const current = state.currentMail;
  if (!current) throw new Error("Cannot record judgment without a current mail.");

  const anomalyUseCounts = { ...state.anomalyUseCounts };
  if (current.anomalyId) {
    anomalyUseCounts[current.anomalyId] = (anomalyUseCounts[current.anomalyId] ?? 0) + 1;
  }

  return {
    ...state,
    currentMail: null,
    currentMailIndex: state.currentMailIndex + 1,
    seenMailIds: [...state.seenMailIds, current.id],
    seenVariantIds: [...state.seenVariantIds, current.variantId],
    usedAnomalyIds: current.anomalyId
      ? [...new Set([...state.usedAnomalyIds, current.anomalyId])]
      : state.usedAnomalyIds,
    anomalyUseCounts,
    discoveredAnomalyIds:
      correct && current.anomalyId
        ? [...new Set([...state.discoveredAnomalyIds, current.anomalyId])]
        : state.discoveredAnomalyIds,
    history: [
      ...state.history,
      {
        mail: current,
        playerSelectedAnomaly,
        correct,
        judgedAt: new Date().toISOString()
      }
    ],
    lastUpdatedAt: new Date().toISOString()
  };
}

export function judgeMail(state: GameState, playerSelectedAnomaly: boolean): JudgmentResult {
  const current = state.currentMail;
  if (!current) throw new Error("No current mail to judge.");

  if (state.phase === "final") {
    const ending = resolveFinalEnding(state, playerSelectedAnomaly);
    const recorded = recordJudgment(state, playerSelectedAnomaly, playerSelectedAnomaly === current.isAnomaly);
    const ended: GameState = {
      ...recorded,
      phase: "ending",
      endingId: ending.id,
      flags: { ...recorded.flags, final_choice_anomaly: playerSelectedAnomaly }
    };
    return { state: ended, correct: playerSelectedAnomaly === current.isAnomaly, enteredLoop: false, reachedFinal: false, reachedEnding: true };
  }

  const correct = current.isAnomaly === playerSelectedAnomaly;
  let next = recordJudgment(state, playerSelectedAnomaly, correct);

  if (correct) {
    next = {
      ...next,
      progress: next.progress + 1,
      correctCount: next.correctCount + 1
    };
  } else {
    next = {
      ...next,
      progress: 0,
      loopCount: next.loopCount + 1,
      mistakeCount: next.mistakeCount + 1,
      flags: { ...next.flags, last_judgment_wrong: true }
    };
  }

  next = { ...next, phase: resolvePhase(next) };
  const reachedFinal = next.phase === "final";
  next = generateNextMail(next);

  return {
    state: next,
    correct,
    enteredLoop: !correct,
    reachedFinal,
    reachedEnding: false
  };
}

export function mergeRunIntoSave(save: SaveData, run: GameState): SaveData {
  const ended = run.phase === "ending";
  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - new Date(run.startedAt).getTime()) / 1000));
  const profile = {
    ...save.profile,
    totalRuns: save.profile.totalRuns + (ended ? 1 : 0),
    totalMistakes: save.profile.totalMistakes + (ended ? run.mistakeCount : 0),
    endings: run.endingId ? [...new Set([...save.profile.endings, run.endingId])] : save.profile.endings,
    discoveredAnomalyIds: [...new Set([...save.profile.discoveredAnomalyIds, ...run.discoveredAnomalyIds])],
    bestClearSeconds:
      ended && (save.profile.bestClearSeconds === undefined || elapsedSeconds < save.profile.bestClearSeconds)
        ? elapsedSeconds
        : save.profile.bestClearSeconds
  };

  return {
    ...save,
    profile,
    currentRun: ended ? undefined : run
  };
}
